import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, Camera, CheckCircle2, Upload, XCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function MemoriesContribute() {
  const { slug, token } = useParams();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState('');
  const [name, setName] = useState('');
  const [queue, setQueue] = useState([]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(0);
  const [failed, setFailed] = useState([]);
  const fileRef = useRef(null);

  useEffect(() => {
    fetch(`${API}/api/pollen/public/brochure/${slug}/contribute-info/${token}`)
      .then(async (r) => {
        if (!r.ok) throw new Error('This upload link is not active anymore.');
        setInfo(await r.json());
      })
      .catch((e) => setErr(e.message));
  }, [slug, token]);

  const pick = (e) => {
    const files = Array.from(e.target.files || []);
    const ok = files.filter((f) => (f.type.startsWith('image/') || f.type.startsWith('video/')) && f.size <= 50 * 1024 * 1024);
    setQueue(ok);
    setDone(0);
    setFailed([]);
    if (files.length !== ok.length) setErr('Some files were skipped (only photos/videos up to 50 MB).');
    else setErr('');
  };

  const upload = async () => {
    if (!queue.length) return;
    setBusy(true);
    setFailed([]);
    let ok = 0;
    const bad = [];
    for (const f of queue) {
      try {
        const fd = new FormData();
        fd.append('uploader_name', name);
        fd.append('file', f);
        const r = await fetch(`${API}/api/pollen/public/brochure/${slug}/contribute/${token}`, { method: 'POST', body: fd });
        if (!r.ok) throw new Error();
        ok += 1;
        setDone(ok);
      } catch {
        bad.push(f.name);
      }
    }
    setFailed(bad);
    setBusy(false);
    if (ok > 0) setQueue([]);
  };

  const finished = done > 0 && !busy && queue.length === 0;

  return (
    <div className="min-h-screen" style={{ background: '#FCF8F7', fontFamily: 'Georgia, serif', color: '#3f2e3e' }} data-testid="memories-contribute-page">
      <div className="max-w-md mx-auto px-5 py-10">
        <p className="text-center text-[11px] tracking-[0.25em] text-[#B16E32]">TRAVEL · CONNECT · CARE</p>
        <p className="text-center text-xs text-[#9a8190] mt-1">pollendots.com</p>

        {err && !info && (
          <div className="mt-10 text-center" data-testid="contribute-error">
            <XCircle className="w-8 h-8 mx-auto text-rose-400" />
            <p className="mt-3 text-sm">{err}</p>
          </div>
        )}
        {!info && !err && <div className="mt-16 text-center"><Loader2 className="w-6 h-6 animate-spin inline text-[#B16E32]" /></div>}

        {info && (
          <>
            <h1 className="text-center italic text-[26px] leading-snug mt-6" data-testid="contribute-title">{info.title}</h1>
            <p className="text-center text-sm text-[#6b5566] mt-1">
              {finished ? 'Thank you for adding to our shared story 🌸' : 'Add your photos & videos to our shared album 🌸'}
            </p>

            {finished ? (
              <div className="mt-8 bg-white rounded-2xl border border-[#EBD9CD] p-6 text-center shadow-sm" data-testid="contribute-success">
                <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-600" />
                <p className="mt-3 font-semibold">{done} {done === 1 ? 'memory' : 'memories'} added to the album</p>
                {failed.length > 0 && <p className="text-xs text-rose-600 mt-1">{failed.length} failed — try those again.</p>}
                <div className="mt-4 flex flex-col gap-2">
                  <button type="button" className="w-full py-3 rounded-full text-white text-sm" style={{ background: '#B16E32' }}
                    onClick={() => { setDone(0); fileRef.current && (fileRef.current.value = ''); }} data-testid="contribute-add-more">
                    Add more
                  </button>
                  {info.album_url && (
                    <a href={info.album_url} className="w-full py-3 rounded-full text-sm border border-[#B16E32] text-[#B16E32]" data-testid="contribute-view-album">
                      View the album →
                    </a>
                  )}
                </div>
              </div>
            ) : (
              <div className="mt-8 bg-white rounded-2xl border border-[#EBD9CD] p-5 shadow-sm space-y-4">
                <div>
                  <label className="text-xs text-[#6b5566]">Your name (shows under your photos)</label>
                  <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Ankita"
                    className="w-full mt-1 px-3 py-2.5 rounded-lg border border-[#EBD9CD] text-sm outline-none focus:border-[#B16E32]"
                    data-testid="contribute-name-input" />
                </div>
                <input ref={fileRef} type="file" multiple accept="image/*,video/*" onChange={pick} className="hidden" id="contrib-files" data-testid="contribute-file-input" />
                <label htmlFor="contrib-files"
                  className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-[#E0C9B8] rounded-xl py-8 cursor-pointer hover:bg-[#FDF8F2]"
                  data-testid="contribute-dropzone">
                  <Camera className="w-7 h-7 text-[#B16E32]" />
                  <span className="text-sm">{queue.length ? `${queue.length} file${queue.length === 1 ? '' : 's'} selected` : 'Tap to choose photos & videos'}</span>
                  <span className="text-[11px] text-[#9a8190]">Up to 50 MB each</span>
                </label>
                {err && <p className="text-xs text-rose-600">{err}</p>}
                {failed.length > 0 && <p className="text-xs text-rose-600">Failed: {failed.join(', ')} — try again.</p>}
                <button type="button" disabled={!queue.length || busy} onClick={upload}
                  className="w-full py-3.5 rounded-full text-white text-sm disabled:opacity-40 flex items-center justify-center gap-2"
                  style={{ background: '#581C2B' }} data-testid="contribute-upload-btn">
                  {busy ? <><Loader2 className="w-4 h-4 animate-spin" />Uploading {done + 1} of {queue.length}…</>
                    : <><Upload className="w-4 h-4" />Add to the album</>}
                </button>
              </div>
            )}
          </>
        )}
        <p className="text-center text-[10px] text-[#9a8190] mt-8">Collected with care by Pollen Dots · pollendots.com</p>
      </div>
    </div>
  );
}
